import {
    type EditorDataSlice,
    type HistoryEntry,
    usePageEditorStore,
} from 'app/store/editor';
import { useBuyerBrandStore } from 'app/store/brand';
import cloneDeep from 'lodash-es/cloneDeep';
import omit from 'lodash-es/omit';
import { useShallow } from 'zustand/react/shallow';
import { getLayout, getLayoutById, postLayout, putLayout } from 'app/apis/layout';
import { getPageById, getPageMeta } from 'app/apis/page';
import { columnCountMap, GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid/rules';
import { LayoutTransformer } from 'shop-ui/tools/LayoutTransformer';
import { NodeStyleManager } from 'shop-ui/tools/NodeStyleManager';
import { NodeContentManager } from 'shop-ui/tools/NodeContentManager';
import { instance as viewportElementCollector } from 'shop-ui/tools/ViewportElementCollector';
import { transformToBasicClientLayout } from 'shop-ui/tools/utils';
import { generateElement } from 'shop-ui/component/generateElement';
import { defaultSiteStyle } from 'shop-ui/data';
import {
    type LayoutNodeStyle,
    type LayoutSiteStyleMap,
    type FlatNode,
    type PageLayoutDetail,
    type LayoutNodeContentMap,
    type FlatMap,
    type PageLayoutData,
    type LayoutNodeStyleMap,
} from 'app/shop-ui/types';
import { FEATURE_FLAG_KEYS, LAYOUT_SECTION_NAME, ELEMENT_TYPE } from '../constants';

export const useLayoutController = (readonly = false) => {
    const editorStore = usePageEditorStore(
        useShallow((api) => ({
            setIsLoading: api.setIsLoading,
            getFlatMap: api.getFlatMap,
            getRootSections: api.getRootSections,
            getStyleMap: api.getStyleMap,
            getContentMap: api.getContentMap,
            getSiteStyle: api.getSiteStyle,
            getPageLayoutMeta: api.getPageLayoutMeta,
            getEditingSectionName: api.getEditingSectionName,
            getNextInsertSectionId: api.getNextInsertSectionId,
            getEditingLayoutId: api.getEditingLayoutId,
            setRootSections: api.setRootSections,
            setFlatMap: api.setFlatMap,
            setStyleMap: api.setStyleMap,
            setContentMap: api.setContentMap,
            setSiteStyle: api.setSiteStyle,
            setPageLayoutMeta: api.setPageLayoutMeta,
        }))
    );

    const brandStore = useBuyerBrandStore(
        useShallow((api) => ({
            setPageSegment: api.setPageSegment,
        }))
    );

    function getNode(id: string): FlatNode;
    function getNode(id: string[]): FlatNode[];
    function getNode(id: string | string[]): FlatNode | FlatNode[] {
        if (Array.isArray(id)) {
            return id.map((_id) => editorStore.getFlatMap()[_id]);
        }

        return editorStore.getFlatMap()[id];
    }

    function getNodeStyle(id: string, type?: undefined): LayoutNodeStyle;
    function getNodeStyle(
        id: string,
        type?: FEATURE_FLAG_KEYS
    ): LayoutNodeStyle[keyof LayoutNodeStyle];
    function getNodeStyle(
        id: string,
        type?: FEATURE_FLAG_KEYS
    ): LayoutNodeStyle | LayoutNodeStyle[keyof LayoutNodeStyle] {
        if (type) {
            return NodeStyleManager.getNodeStyleByType(
                editorStore.getStyleMap(),
                id,
                type
            );
        }

        return NodeStyleManager.getNodeStyle(editorStore.getStyleMap(), id);
    }

    function getNodeContent(id: string) {
        return NodeContentManager.getNodeContent(editorStore.getContentMap(), id);
    }

    function _getPageLayoutMeta(sectionName: LAYOUT_SECTION_NAME) {
        return (
            editorStore.getPageLayoutMeta()[sectionName] ?? {
                layoutId: '',
                sectionName,
                pageId: '',
            }
        );
    }

    function setRootSections(rootSections: EditorDataSlice['rootSections']) {
        if (readonly) {
            return;
        }

        editorStore.setRootSections(rootSections);
    }

    function setNodeStyle(
        input: { id: string; style: LayoutNodeStyle }[],
        value?: undefined
    );
    function setNodeStyle(input: string, value: LayoutNodeStyle);
    function setNodeStyle(
        input: { id: string; style: LayoutNodeStyle }[] | string,
        value?: LayoutNodeStyle
    ) {
        if (readonly) {
            return;
        }

        if (Array.isArray(input)) {
            const newStyleMap = { ...editorStore.getStyleMap() };

            for (const { id, style: newStyle } of input) {
                newStyleMap[id] = {
                    ...newStyleMap[id],
                    ...newStyle,
                };
            }

            editorStore.setStyleMap(newStyleMap);
        }

        const id = input;

        const newStyle = value;

        if (typeof id !== 'string' || !newStyle) {
            throw new Error('Invalid input');
        }

        editorStore.setStyleMap((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                ...newStyle,
            },
        }));
    }

    function setNodeContent(id: string, content: string) {
        if (readonly) {
            return;
        }

        editorStore.setContentMap((prev) => ({
            ...prev,
            [id]: content,
        }));
    }

    function setSiteStyle(styleUpdates: LayoutSiteStyleMap) {
        if (readonly) {
            return;
        }

        editorStore.setSiteStyle((prev) => ({
            ...prev,
            ...styleUpdates,
        }));
    }

    function setNode(id: string, nodeMeta: Partial<FlatNode>) {
        if (readonly) {
            return;
        }

        editorStore.setFlatMap((prev) => {
            const newFlatMap = { ...prev };

            newFlatMap[id] = {
                ...prev[id],
                ...nodeMeta,
            };

            return newFlatMap;
        });
    }

    function deleteNode(id: string) {
        const sectionName = LAYOUT_SECTION_NAME.BODY;

        const prevNode = editorStore.getFlatMap()[id];

        const pageLayoutMeta = editorStore.getPageLayoutMeta();

        if (!prevNode || !pageLayoutMeta[sectionName]) {
            return null;
        }

        const result = {
            newRootIds: [],
            newFlatMap: {},
            newStyleMap: {},
            newContentMap: {},
            newRootSections: {},
            nextEditingDetail: {
                id: prevNode.id,
                type: prevNode.type,
                layoutId: pageLayoutMeta[sectionName].layoutId,
                sectionName,
            },
        } as Omit<NonNullable<ReturnType<typeof newNode>>, 'historyRecord'>;

        const historyRecord = {
            parentId: null,
            targetIndex: -1,
            targetNodeId: id,
            targetSectionName: sectionName,
            nodesUpdated: {},
            nodesStyleUpdated: {},
            nodesContentUpdated: {},
        } as Omit<
            Extract<HistoryEntry, { type: 'batch+remove' }>,
            'type' | 'resultForPostMessage'
        >; // 給 history 用的資訊

        const { nodeChain, nodeParentId, ancestorChain } = LayoutTransformer.getNodeChain(
            id,
            editorStore.getFlatMap()
        );

        let nextRootId = ancestorChain[0];

        historyRecord.parentId = nodeParentId;

        editorStore.setRootSections((prev) => {
            const newRootSections = cloneDeep(prev);

            for (const rootSection of Object.keys(newRootSections)) {
                const rootIds = newRootSections[rootSection as LAYOUT_SECTION_NAME];

                const removeIndex = rootIds.indexOf(id);

                if (removeIndex !== -1) {
                    nextRootId = rootIds[removeIndex + 1] || rootIds[removeIndex - 1];

                    rootIds.splice(removeIndex, 1);

                    historyRecord.targetIndex = removeIndex;

                    result.newRootIds = prev[rootSection as LAYOUT_SECTION_NAME];
                }
            }

            result.newRootSections = prev;

            return newRootSections;
        });

        editorStore.setFlatMap((prev) => {
            const newFlatMap = cloneDeep(prev);

            historyRecord.nodesUpdated[id] = prev[id];

            delete newFlatMap[id];

            for (const nodeId of nodeChain) {
                historyRecord.nodesUpdated[nodeId] = prev[nodeId];

                delete newFlatMap[nodeId];
            }

            if (nodeParentId) {
                const removeIndex = newFlatMap[nodeParentId].next.indexOf(id);

                if (removeIndex !== -1) {
                    newFlatMap[nodeParentId].next.splice(removeIndex, 1);

                    historyRecord.targetIndex = removeIndex;
                }
            }

            result.newFlatMap = prev;

            return newFlatMap;
        });

        editorStore.setStyleMap((prev) => {
            const newStyleMap = cloneDeep(prev);

            for (const nodeId of nodeChain) {
                historyRecord.nodesStyleUpdated[nodeId] = prev[nodeId];

                delete newStyleMap[nodeId];
            }

            result.newStyleMap = prev;

            return newStyleMap;
        });

        editorStore.setContentMap((prev) => {
            const newContentMap = cloneDeep(prev);

            for (const nodeId of nodeChain) {
                historyRecord.nodesContentUpdated[nodeId] = prev[nodeId];

                delete newContentMap[nodeId];
            }

            result.newContentMap = prev;

            return newContentMap;
        });

        for (const nodeId of nodeChain) {
            if (viewportElementCollector._elements.has(nodeId)) {
                viewportElementCollector.unsubscribe(nodeId, true);
            }
        }

        return {
            nextRootId,
            ...result,
            historyRecord,
        };
    }

    function getSelectableIdSet(selectedElementId: string | null) {
        const rootIds = Object.values(editorStore.getRootSections()).flatMap((id) => id);

        return LayoutTransformer.getSelectableIdSet(
            selectedElementId,
            editorStore.getFlatMap(),
            rootIds
        );
    }

    function getRootId(id: string | null) {
        return LayoutTransformer.getRootId(id, editorStore.getFlatMap());
    }

    function duplicate(elementId: string, sectionName: LAYOUT_SECTION_NAME) {
        const historyRecord = {
            parentId: null,
            targetIndex: -1,
            targetNodeId: '',
            targetSectionName: sectionName,
            nodesUpdated: {},
            nodesStyleUpdated: {},
            nodesContentUpdated: {},
        } as Omit<
            Extract<HistoryEntry, { type: 'batch+remove' }>,
            'type' | 'resultForPostMessage'
        >; // 給 history 用的資訊

        const flatMap = editorStore.getFlatMap();

        const newStyleMap = editorStore.getStyleMap();

        const newContentMap = editorStore.getContentMap();

        const newRootSections = editorStore.getRootSections();

        const { newFlatMap, newElementId, originalIdToNewIdMapping } =
            LayoutTransformer.duplicate(flatMap, elementId);

        for (const [originalId, newId] of originalIdToNewIdMapping) {
            if (newStyleMap[originalId]) {
                newStyleMap[newId] = cloneDeep(newStyleMap[originalId]);

                historyRecord.nodesStyleUpdated[newId] = newStyleMap[newId];
            }

            if (newContentMap[originalId]) {
                newContentMap[newId] = cloneDeep(newContentMap[originalId]);

                historyRecord.nodesContentUpdated[newId] = newContentMap[newId];
            }
        }

        historyRecord.targetNodeId = newElementId;

        const cloneNodeParentId = newFlatMap[newElementId].parentId;

        const isRootClone = cloneNodeParentId === null;

        if (isRootClone) {
            const sourceRootIdx = newRootSections[sectionName].findIndex(
                (id) => id === elementId
            );

            if (sourceRootIdx !== -1) {
                newRootSections[sectionName].splice(sourceRootIdx + 1, 0, newElementId);

                historyRecord.targetIndex = sourceRootIdx;
            } else {
                newRootSections[sectionName].push(newElementId);

                historyRecord.targetIndex = newRootSections[sectionName].length - 1;
            }

            editorStore.setRootSections((prev) => ({
                ...prev,
                [sectionName]: [...newRootSections[sectionName]],
            }));
        } else {
            historyRecord.parentId = cloneNodeParentId;

            const sourceNodeIdx = newFlatMap[cloneNodeParentId].next.findIndex(
                (id) => id === elementId
            );

            if (sourceNodeIdx !== -1) {
                newFlatMap[cloneNodeParentId].next.splice(
                    sourceNodeIdx + 1,
                    0,
                    newElementId
                );

                historyRecord.targetIndex = sourceNodeIdx;
            } else {
                newFlatMap[cloneNodeParentId].next.push(newElementId);

                historyRecord.targetIndex = newFlatMap[cloneNodeParentId].next.length - 1;
            }
        }

        editorStore.setFlatMap((prev) => {
            const _newFlatMap = {
                ...newFlatMap,
                ...(cloneNodeParentId && {
                    [cloneNodeParentId]: {
                        ...prev[cloneNodeParentId],
                        next: [...prev[cloneNodeParentId].next],
                    },
                }),
            };

            historyRecord.nodesUpdated = _newFlatMap;

            return {
                ...prev,
                ..._newFlatMap,
            };
        });

        editorStore.setStyleMap(newStyleMap);

        editorStore.setContentMap(newContentMap);

        return {
            ...(isRootClone && {
                newRootIds: newRootSections[sectionName],
                newRootSections,
            }),
            newFlatMap,
            newStyleMap: historyRecord.nodesStyleUpdated,
            newContentMap: historyRecord.nodesContentUpdated,
            historyRecord,
            nextEditingDetail: newFlatMap[newElementId],
        };
    }

    function mergeCell(
        index: number,
        layoutValue: GRID_LAYOUT_TYPE,
        editingElementId: string,
        editingSectionName: LAYOUT_SECTION_NAME
    ) {
        const flatMap = editorStore.getFlatMap();

        const styleMap = editorStore.getStyleMap();

        if (
            !editingSectionName ||
            !editingElementId ||
            !flatMap[editingElementId] ||
            !Object.prototype.hasOwnProperty.call(
                styleMap[editingElementId],
                FEATURE_FLAG_KEYS.GRID
            )
        ) {
            return;
        }

        const gridNode = flatMap[editingElementId];

        const targetNodeId = gridNode.next[index];

        const sourceNodeIds = gridNode.next.slice(index + 1);

        const migrateNodeIds: string[] = [];

        let targetCellId: string | null = null;

        if (flatMap[editingElementId].type === ELEMENT_TYPE.GRID_PRIMARY_LEVEL) {
            let targetGridType = styleMap[targetNodeId][FEATURE_FLAG_KEYS.GRID];

            if (!targetGridType) {
                targetGridType = GRID_LAYOUT_TYPE.SINGLE_CELL;
            }

            const tailOfTargetGridCellIndex = columnCountMap[targetGridType] - 1;

            targetCellId = flatMap[targetNodeId].next[tailOfTargetGridCellIndex];

            const migrateNode = (nodeId: string, currentTargetCellId: string) => {
                const nextIds = flatMap[nodeId].next;

                for (const nextId of nextIds) {
                    if (
                        flatMap[nextId].type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL ||
                        flatMap[nextId].type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL_INNER
                    ) {
                        flatMap[nextId].next.forEach((_nextId) => {
                            flatMap[_nextId].parentId = currentTargetCellId;

                            migrateNodeIds.push(_nextId);
                        });

                        flatMap[nextId].next = [];
                    }
                }
            };

            for (const sourceNodeId of sourceNodeIds) {
                migrateNode(sourceNodeId, targetCellId);
            }
        } else if (flatMap[editingElementId].type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL) {
            targetCellId = targetNodeId;

            for (const sourceNodeId of sourceNodeIds) {
                flatMap[sourceNodeId].next.forEach((_nextId) => {
                    flatMap[_nextId].parentId = targetCellId;

                    migrateNodeIds.push(_nextId);
                });

                flatMap[sourceNodeId].next = [];
            }
        }

        if (!targetCellId) {
            console.error('Target cell ID could not be determined');

            return;
        }

        const batchUpdateFlatMap = {
            [targetCellId]: {
                ...flatMap[targetCellId],
                next: [...flatMap[targetCellId].next, ...migrateNodeIds],
            },
        } as FlatMap;

        for (const nodeId of migrateNodeIds) {
            batchUpdateFlatMap[nodeId] = flatMap[nodeId];
        }

        editorStore.setFlatMap((prev) => ({
            ...prev,
            ...cloneDeep(batchUpdateFlatMap),
        }));

        editorStore.setStyleMap((prev) => ({
            ...prev,
            [editingElementId]: {
                ...prev[editingElementId],
                [FEATURE_FLAG_KEYS.GRID]: layoutValue,
            },
        }));
    }

    function newNode(
        name: string,
        content: string,
        isSection?: boolean,
        isDnD?: boolean
    ) {
        // header 和 footer 目前不給新增節點
        const sectionName = LAYOUT_SECTION_NAME.BODY;

        const nextInsertSectionId = editorStore.getNextInsertSectionId();

        // 如果 nextInsertSectionId 有值，表示要插入到 section 裡面
        // 反之，為全新的 section，需被 grid 包覆
        // DnD 拖移時為特例，不會被包覆
        const shouldWrap = !nextInsertSectionId && !isDnD;

        const _newComponent = generateElement(name, content, {
            shouldWrap,
        }) as PageLayoutData;

        if (!_newComponent) {
            return null;
        }

        const historyRecord = {
            parentId: null,
            targetIndex: -1,
            targetNodeId: '',
            targetSectionName: sectionName,
            nodesUpdated: {},
            nodesStyleUpdated: {},
            nodesContentUpdated: {},
        } as Omit<
            Extract<HistoryEntry, { type: 'batch+add' }>,
            'type' | 'resultForPostMessage'
        >; // 給 history 用的資訊

        const { rootIds: newRootIds, flatMap: newFlatMap } =
            LayoutTransformer.toFlatLayout(_newComponent.layout);

        if (isDnD) {
            return {
                newRootIds,
                newFlatMap,
                newStyleMap: _newComponent.style,
                newContentMap: _newComponent.content,
                historyRecord,
            };
        }

        let newRootSections: Partial<EditorDataSlice['rootSections']> | null = null;

        const rootIds = editorStore.getRootSections()[sectionName];

        const insertIndex = nextInsertSectionId
            ? rootIds.indexOf(nextInsertSectionId) + 1
            : rootIds.length;

        if (isSection) {
            rootIds.splice(insertIndex, 0, newRootIds[0]);

            newRootSections = {
                [sectionName]: rootIds,
            };

            historyRecord.targetIndex = insertIndex;

            [historyRecord.targetNodeId] = newRootIds;
        } else {
            const sectionIdIndex = insertIndex - 1;

            const node = editorStore.getFlatMap()[rootIds[sectionIdIndex]];

            const newNodeId = newRootIds[0];

            const findInsertTargetNode = (_node: FlatNode) => {
                if (_node.type === ELEMENT_TYPE.GRID_SECONDARY_LEVEL_INNER) {
                    return _node.id;
                }

                if (_node.next && _node.next.length > 0) {
                    const nextNode = getNode(_node.next[0]);

                    return findInsertTargetNode(nextNode);
                }

                return _node.id;
            };

            const insertTargetNodeId = findInsertTargetNode(node);

            const insertTargetNode = getNode(insertTargetNodeId);

            newFlatMap[insertTargetNodeId] = {
                ...insertTargetNode,
                next: [...insertTargetNode.next, newNodeId],
            };

            newFlatMap[newNodeId].parentId = insertTargetNode.id;

            historyRecord.targetIndex = sectionIdIndex;

            [historyRecord.targetNodeId] = newRootIds;

            historyRecord.parentId = insertTargetNode.id;
        }

        historyRecord.nodesUpdated = newFlatMap;

        historyRecord.nodesStyleUpdated = _newComponent.style;

        historyRecord.nodesContentUpdated = _newComponent.content;

        editorStore.setFlatMap((prev) => ({
            ...prev,
            ...newFlatMap,
        }));

        editorStore.setStyleMap((prev) => ({
            ...prev,
            ..._newComponent.style,
        }));

        editorStore.setContentMap((prev) => ({
            ...prev,
            ..._newComponent.content,
        }));

        if (newRootSections) {
            editorStore.setRootSections((prev) => ({
                ...prev,
                ...newRootSections,
            }));
        }

        const layoutId =
            editorStore.getEditingLayoutId() || _getPageLayoutMeta(sectionName).layoutId;

        const nextEditingDetail = {
            id: newRootIds[0],
            type: newFlatMap[newRootIds[0]].type,
            layoutId,
            sectionName,
        };

        return {
            newRootSections,
            newFlatMap,
            newStyleMap: _newComponent.style,
            newContentMap: _newComponent.content,
            nextEditingDetail,
            historyRecord,
        };
    }

    type NewPageLayoutArgs = {
        pageId: string | null;
        sectionName: LAYOUT_SECTION_NAME;
        layout?: PageLayoutData;
        options?: {
            cb?: (layout: Layout) => void;
        };
    };

    async function newPageLayout(data: NewPageLayoutArgs) {
        const { sectionName, layout = {}, pageId, options } = data;

        const resp = await postLayout({
            section: sectionName,
            pageId,
            layout: {
                ...layout,
                ...(sectionName === LAYOUT_SECTION_NAME.BODY && {
                    site: defaultSiteStyle,
                }),
            },
        });

        if (resp) {
            options?.cb?.(resp.data);
        }

        return resp;
    }

    type DangerouslyNewNodeArgs = {
        layoutId: string;
        pageId: string | null;
        sectionName: string;
        layout: PageLayoutData;
    };

    async function dangerouslyNewNode(args: DangerouslyNewNodeArgs) {
        const resp = await putLayout({
            layoutId: args.layoutId,
            section: args.sectionName,
            pageId: args.pageId,
            layout: args.layout,
        });

        return resp;
    }

    async function preparePageSegment(styleMap: LayoutNodeStyleMap) {
        const pageIds = LayoutTransformer.preparePageMetaIds(styleMap);

        if (!pageIds.length) {
            return {};
        }

        const cachedPageSegment = {} as Record<string, string>;

        const result = await getPageMeta({
            pageIds,
        });

        if (result?.data) {
            for (const [id, pageMeta] of Object.entries(result.data)) {
                cachedPageSegment[id] = pageMeta.segment;
            }
        }

        return cachedPageSegment;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function initialize(data: any, listener?: (percent: number) => void) {
        const flatLayoutMapBySectionName = {
            [LAYOUT_SECTION_NAME.HEADER]: null,
            [LAYOUT_SECTION_NAME.FOOTER]: null,
            [LAYOUT_SECTION_NAME.BODY]: null,
        } as {
            [key in LAYOUT_SECTION_NAME]: PageLayoutDetail | null;
        };

        const requiredKeys = ['layout', 'style', 'content', 'site'] as const;

        editorStore.setIsLoading(true);

        function generateMissingKeys(layout) {
            const missingKeys = {};

            for (const key of requiredKeys) {
                if (!Object.prototype.hasOwnProperty.call(layout, key)) {
                    missingKeys[key] = true;
                }
            }

            if (data.section !== LAYOUT_SECTION_NAME.BODY && 'site' in missingKeys) {
                delete missingKeys.site;
            }

            return missingKeys;
        }

        function createNewLayoutData(missingKeys) {
            const newLayoutData = {};

            if (missingKeys.layout) {
                // @ts-ignore
                newLayoutData.layout = [];
            }

            if (missingKeys.style) {
                // @ts-ignore
                newLayoutData.style = {};
            }

            if (missingKeys.content) {
                // @ts-ignore
                newLayoutData.content = {};
            }

            if (missingKeys.site) {
                // @ts-ignore
                newLayoutData.site = defaultSiteStyle;
            }

            return newLayoutData;
        }

        async function processLayout(layout, pageId) {
            const { section: layoutSectionName, id: layoutId } = layout;

            try {
                const layoutResp = await getLayoutById({
                    layoutId,
                    pageId,
                    section: layoutSectionName,
                });

                if (!layoutResp) {
                    return;
                }

                const sectionData = transformToBasicClientLayout(layoutResp.data);

                const missingKeys = generateMissingKeys(sectionData.layout);

                flatLayoutMapBySectionName[layoutSectionName] = sectionData;

                if (Object.keys(missingKeys).length > 0) {
                    const newLayoutData = createNewLayoutData(missingKeys);

                    await putLayout({
                        layoutId,
                        section: layoutSectionName,
                        pageId,
                        layout: {
                            ...sectionData.layout,
                            ...newLayoutData,
                        },
                    }).then((resp) => {
                        if (resp) {
                            flatLayoutMapBySectionName[layoutSectionName] =
                                transformToBasicClientLayout(resp.data);
                        }
                    });
                }
            } catch {
                flatLayoutMapBySectionName[layoutSectionName] = null;
            }
        }

        if (typeof data === 'string') {
            const pageId = data;

            try {
                const resp = await getPageById({ pageId });

                if (resp?.data?.layouts) {
                    await Promise.allSettled(
                        resp.data.layouts.map((layout) => processLayout(layout, pageId))
                    );
                }

                await Promise.allSettled(
                    [LAYOUT_SECTION_NAME.HEADER, LAYOUT_SECTION_NAME.FOOTER].map(
                        async (sectionName) => {
                            try {
                                const layoutResp = await getLayout({
                                    section: sectionName,
                                });

                                if (!layoutResp || !layoutResp.data[0]) throw new Error();

                                const layout = layoutResp.data[0];

                                await processLayout(layout, '');
                            } catch {
                                flatLayoutMapBySectionName[sectionName] = null;
                            }
                        }
                    )
                );
            } catch {
                editorStore.setIsLoading(false);
            }
        } else {
            flatLayoutMapBySectionName[LAYOUT_SECTION_NAME.HEADER] = data.header;

            flatLayoutMapBySectionName[LAYOUT_SECTION_NAME.FOOTER] = data.footer;

            flatLayoutMapBySectionName[LAYOUT_SECTION_NAME.BODY] = data.body;
        }

        const headerFlatLayout = LayoutTransformer.toFlatLayout(
            flatLayoutMapBySectionName.header?.layout.layout ?? []
        );

        listener?.(20);

        const footerFlatLayout = LayoutTransformer.toFlatLayout(
            flatLayoutMapBySectionName.footer?.layout.layout ?? []
        );

        listener?.(40);

        const bodyFlatLayout = LayoutTransformer.toFlatLayout(
            flatLayoutMapBySectionName.body?.layout.layout ?? []
        );

        listener?.(60);

        const styleMap = {
            ...(flatLayoutMapBySectionName.header?.layout.style ?? {}),
            ...(flatLayoutMapBySectionName.footer?.layout.style ?? {}),
            ...(flatLayoutMapBySectionName.body?.layout.style ?? {}),
        } as LayoutNodeStyleMap;

        const contentMap = {
            ...(flatLayoutMapBySectionName.header?.layout.content ?? {}),
            ...(flatLayoutMapBySectionName.footer?.layout.content ?? {}),
            ...(flatLayoutMapBySectionName.body?.layout.content ?? {}),
        } as LayoutNodeContentMap;

        const siteStyle =
            flatLayoutMapBySectionName.body?.layout.site ?? ({} as LayoutSiteStyleMap);

        const pageLayoutMeta = {
            [LAYOUT_SECTION_NAME.HEADER]: omit(flatLayoutMapBySectionName.header, [
                'layout',
            ]),
            [LAYOUT_SECTION_NAME.FOOTER]: omit(flatLayoutMapBySectionName.footer, [
                'layout',
            ]),
            [LAYOUT_SECTION_NAME.BODY]: omit(flatLayoutMapBySectionName.body, ['layout']),
        };

        listener?.(70);

        const cachedPageMeta = await preparePageSegment(styleMap);

        listener?.(80);

        editorStore.setFlatMap({
            ...headerFlatLayout.flatMap,
            ...footerFlatLayout.flatMap,
            ...bodyFlatLayout.flatMap,
        });

        editorStore.setRootSections({
            header: headerFlatLayout.rootIds,
            footer: footerFlatLayout.rootIds,
            body: bodyFlatLayout.rootIds,
        });

        editorStore.setStyleMap(styleMap);

        editorStore.setContentMap(contentMap);

        editorStore.setSiteStyle(siteStyle);

        editorStore.setPageLayoutMeta(pageLayoutMeta);

        brandStore.setPageSegment((prev) => {
            return {
                ...prev,
                ...cachedPageMeta,
            };
        });

        listener?.(100);

        editorStore.setIsLoading(false);

        return flatLayoutMapBySectionName;
    }

    return {
        getNode,
        getNodeStyle,
        getNodeContent,
        getSiteStyle: editorStore.getSiteStyle,
        getPageLayoutMeta: _getPageLayoutMeta,
        getSelectableIdSet,
        setRootSections,
        setNode,
        setNodeStyle,
        setNodeContent,
        setSiteStyle,
        deleteNode,
        getRootId,
        duplicate,
        mergeCell,
        newNode,
        newPageLayout,
        dangerouslyNewNode,
        initialize,
    };
};
